import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import aiService from '../services/aiService';
import AIConversation from '../components/AIConversation';
import BackToDashboard from '../components/BackToDashboard';

function IrresistibleOffer() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // Stages: intro, conversation, guarantee, generating, recommendations, customizing
  const [stage, setStage] = useState('intro');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Business context
  const [coreProduct, setCoreProduct] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [price, setPrice] = useState('');
  const [usp, setUSP] = useState('');

  // Interview conversation
  const [conversation, setConversation] = useState([]);
  const [initialMessage, setInitialMessage] = useState('');

  // Guarantee selection
  const [guaranteeType, setGuaranteeType] = useState('');
  const [guaranteeConditions, setGuaranteeConditions] = useState('');

  // Offer data
  const [aiOffer, setAiOffer] = useState(null);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load business context
    try {
      const responses = await Promise.all([
        fetch('/api/products/get-catalog', { credentials: 'include' }),
        fetch('/api/business/field/target_audience', { credentials: 'include' }),
        fetch('/api/business/field/pricing_data', { credentials: 'include' }),
        fetch('/api/business/field/usp', { credentials: 'include' })
      ]);

      const [productsData, audienceData, pricingData, uspData] = await Promise.all(
        responses.map(r => r.json())
      );

      // Get core product from Product table
      const products = productsData.catalog || [];
      let coreProduct = products.find(p => p.position?.toLowerCase() === 'core');
      if (!coreProduct && products.length > 0) {
        coreProduct = products.find(p => !p.is_primary_tripwire && p.position?.toLowerCase() !== 'tripwire');
      }
      setCoreProduct(coreProduct?.name || '');

      setTargetAudience(audienceData.field_value || '');
      setUSP(uspData.field_value || '');

      // Extract price from pricing_data
      if (pricingData.field_value) {
        try {
          const pricing = JSON.parse(pricingData.field_value);
          const extractedPrice = pricing.monthly_price || pricing.annual_price ||
                                pricing.one_time_price || pricing.hybrid_monthly || '';
          setPrice(extractedPrice);
        } catch (err) {
          console.error('Error parsing pricing data:', err);
        }
      }

      // Check if user has saved offer
      const offerResponse = await fetch('/api/irresistible-offer/get', {
        credentials: 'include'
      });

      if (offerResponse.ok) {
        const offerData = await offerResponse.json();
        if (offerData.offer && Object.keys(offerData.offer).length > 0) {
          setOffer(offerData.offer);
          setAiOffer(offerData.ai_offer);
          setStage('customizing');
          setLoading(false);
          return;
        }
      }

      // Check for saved conversation to resume
      const conversationResult = await aiService.getIrresistibleOfferConversation();
      if (conversationResult.success && conversationResult.data.conversation && conversationResult.data.conversation.length > 1) {
        setConversation(conversationResult.data.conversation);
        // Find the last assistant message for initialMessage
        const lastAssistant = [...conversationResult.data.conversation].reverse().find(m => m.role === 'assistant');
        if (lastAssistant) {
          setInitialMessage(lastAssistant.content);
        }
        setStage('conversation');
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }

    setLoading(false);
  };

  // Start the interview
  const handleStartInterview = async () => {
    setError('');
    setLoading(true);

    const result = await aiService.startIrresistibleOfferInterview();

    if (result.success) {
      setConversation(result.data.conversation);
      setInitialMessage(result.data.message);
      setStage('conversation');
    } else {
      setError(result.error || 'Failed to start interview');
    }

    setLoading(false);
  };

  // Handle message in conversation
  const handleSendMessage = async (userMessage, messageHistory) => {
    const result = await aiService.respondIrresistibleOfferInterview(userMessage, conversation);

    if (result.success) {
      setConversation(result.data.conversation);

      // Check if interview is complete
      if (result.data.is_complete) {
        return {
          message: result.data.message,
          isComplete: true
        };
      }

      return { message: result.data.message };
    } else {
      return { error: result.error || 'Failed to send message' };
    }
  };

  // Handle conversation completion - go directly to generating (AI already asked about guarantee)
  const handleConversationComplete = () => {
    setStage('generating');
    // Generate immediately - guarantee info is in the conversation
    generateOfferFromConversation();
  };

  const generateOfferFromConversation = async () => {
    setError('');

    try {
      const result = await aiService.generateIrresistibleOfferFromInterview(
        conversation,
        '', // Guarantee info is in the conversation
        ''
      );

      if (result.success) {
        setAiOffer(result.data.offer);
        setOffer(JSON.parse(JSON.stringify(result.data.offer))); // Deep copy
        setStage('recommendations');
      } else {
        setError(`Failed to generate offer: ${result.error || 'Unknown error'}`);
        setStage('intro');
      }
    } catch (err) {
      setError('Error generating offer: ' + err.message);
      setStage('intro');
    }
  };

  // Generate offer from interview data
  const handleGenerate = async () => {
    setError('');
    setStage('generating');

    try {
      const result = await aiService.generateIrresistibleOfferFromInterview(
        conversation,
        guaranteeType,
        guaranteeConditions
      );

      if (result.success) {
        setAiOffer(result.data.offer);
        setOffer(JSON.parse(JSON.stringify(result.data.offer))); // Deep copy
        setStage('recommendations');
      } else {
        setError(`Failed to generate offer: ${result.error || 'Unknown error'}`);
        setStage('intro');
      }
    } catch (err) {
      setError('Error generating offer: ' + err.message);
      setStage('intro');
    }
  };

  const handleCustomize = () => {
    setStage('customizing');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/irresistible-offer/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          offer: offer,
          ai_offer: aiOffer
        })
      });

      if (response.ok) {
        navigate('/dashboard/business-model');
      } else {
        const errorData = await response.json();
        setError(`Failed to save offer: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving offer: ' + err.message);
    }

    setSaving(false);
  };

  const updateBonus = (index, field, value) => {
    const updatedBonuses = [...offer.bonus_stack];
    updatedBonuses[index][field] = value;
    setOffer({ ...offer, bonus_stack: updatedBonuses });
  };

  const addBonus = () => {
    const newBonus = {
      name: '',
      description: '',
      perceived_value: '$'
    };
    setOffer({ ...offer, bonus_stack: [...offer.bonus_stack, newBonus] });
  };

  const removeBonus = (index) => {
    const updatedBonuses = offer.bonus_stack.filter((_, i) => i !== index);
    setOffer({ ...offer, bonus_stack: updatedBonuses });
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  // Screen 1: Intro - Explain the interview process
  if (stage === 'intro') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="mb-4">Create Your Irresistible Offer</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  <p className="mb-2">
                    <strong>An irresistible offer</strong> transforms a good product into something customers can't wait to buy.
                  </p>
                  <p className="mb-0">
                    I'll interview you to discover your <strong>real differentiators</strong> - what makes your offer unique.
                    Then I'll polish your answers into compelling marketing copy.
                  </p>
                </Alert>

                <Card className="bg-light mb-4">
                  <Card.Body>
                    <h5 className="mb-3">We'll cover 4 topics:</h5>
                    <ol className="mb-0">
                      <li className="mb-2"><strong>Above & Beyond</strong> - Where you exceed what competitors offer</li>
                      <li className="mb-2"><strong>Bonus Items</strong> - Tangible extras customers keep (PDFs, templates, books)</li>
                      <li className="mb-2"><strong>Guarantee</strong> - How long you'll stand behind your offer</li>
                      <li className="mb-2"><strong>Urgency</strong> - How you'll create genuine FOMO</li>
                    </ol>
                  </Card.Body>
                </Card>

                <Card className="bg-light mb-4">
                  <Card.Body>
                    <h5 className="mb-3">Your Business Context:</h5>
                    <div className="mb-2"><strong>Core Product:</strong> {coreProduct || 'Not set'}</div>
                    <div className="mb-2"><strong>Target Audience:</strong> {targetAudience || 'Not set'}</div>
                    <div className="mb-2"><strong>Price:</strong> ${price || 'Not set'}</div>
                    <div className="mb-2"><strong>USP:</strong> {usp || 'Not set'}</div>
                  </Card.Body>
                </Card>

                <div className="text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleStartInterview}
                    className="px-5 mb-3"
                    disabled={loading}
                  >
                    {loading ? 'Starting...' : 'Start Interview →'}
                  </Button>

                  <div>
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/dashboard/business-model')}
                    >
                      ← Back to Dashboard
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 2: Conversation - AI Interview
  if (stage === 'conversation') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={9}>
            <AIConversation
              title="Irresistible Offer Interview"
              subtitle="Let's discover what makes your offer unique"
              initialMessage={initialMessage}
              onSendMessage={handleSendMessage}
              onComplete={handleConversationComplete}
              onCancel={() => navigate('/dashboard/business-model')}
              maxExchanges={4}
              doneButtonText="Finish Interview"
              placeholder="Type your response..."
            />
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 3: Guarantee Selection
  if (stage === 'guarantee') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="mb-4">Select Your Guarantee</h2>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="info" className="mb-4">
                  <strong>Did you know?</strong> Credit card companies allow chargebacks for up to 6 months.
                  So guarantees shorter than 6 months don't actually reduce your risk much - but they DO reduce buyer confidence.
                </Alert>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Guarantee Duration</strong></Form.Label>
                  <Form.Select
                    value={guaranteeType}
                    onChange={(e) => setGuaranteeType(e.target.value)}
                    size="lg"
                  >
                    <option value="">Select a guarantee duration...</option>
                    <option value="30-day money-back">30-Day Money-Back Guarantee</option>
                    <option value="60-day money-back">60-Day Money-Back Guarantee</option>
                    <option value="90-day money-back">90-Day Money-Back Guarantee</option>
                    <option value="6-month money-back">6-Month Money-Back Guarantee</option>
                    <option value="1-year money-back">1-Year Money-Back Guarantee</option>
                    <option value="lifetime money-back">Lifetime Money-Back Guarantee</option>
                    <option value="satisfaction guarantee">Satisfaction Guarantee</option>
                    <option value="results-based guarantee">Results-Based Guarantee</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label><strong>Guarantee Conditions (Optional)</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={guaranteeConditions}
                    onChange={(e) => setGuaranteeConditions(e.target.value)}
                    placeholder="e.g., 'Must complete all modules', 'Must implement for 30 days', 'Results must be documented'"
                  />
                  <Form.Text className="text-muted">
                    Add any conditions for your guarantee, or leave blank for unconditional.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-3 justify-content-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={!guaranteeType}
                  >
                    Generate My Irresistible Offer →
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('conversation')}
                  >
                    ← Back to Interview
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 4: Generating
  if (stage === 'generating') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow p-5">
              <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem', margin: '0 auto' }} />
              <h4>Crafting Your Irresistible Offer...</h4>
              <p className="text-muted mt-3">
                Polishing your interview responses into compelling marketing copy with bonuses, guarantees, urgency, and risk reversal.
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 3: Review Recommendations
  if (stage === 'recommendations' && offer) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Your Irresistible Offer</h2>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <Alert variant="success" className="mb-4">
                  <strong>AI Generated Your Complete Offer!</strong> Review the unified offer below, then scroll down to see how each component works.
                </Alert>

                {/* Complete Unified Offer */}
                <Card className="mb-4 border-primary border-3">
                  <Card.Header className="bg-dark text-white">
                    <h4 className="mb-0">Your Complete Irresistible Offer</h4>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <div style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                      {offer.complete_offer}
                    </div>
                  </Card.Body>
                </Card>

                <Alert variant="info" className="mb-4">
                  <strong>How it works:</strong> The components below show you the building blocks of your offer. Scroll down to see the details.
                </Alert>

                {/* Bonus Stack */}
                <Card className="mb-4">
                  <Card.Header className="bg-dark text-white">
                    <h5 className="mb-0">Bonus Stack</h5>
                  </Card.Header>
                  <Card.Body>
                    {offer.bonus_stack && offer.bonus_stack.map((bonus, index) => (
                      <div key={index} className="mb-3 pb-3 border-bottom">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1">{bonus.name}</h6>
                            <Badge bg="success" className="mb-2">Value: {bonus.perceived_value}</Badge>
                          </div>
                        </div>
                        <p className="mb-0 text-muted">{bonus.description}</p>
                      </div>
                    ))}
                  </Card.Body>
                </Card>

                {/* Guarantee */}
                <Card className="mb-4">
                  <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">Guarantee</h5>
                  </Card.Header>
                  <Card.Body>
                    <Badge bg="info" className="mb-2">{offer.guarantee?.type}</Badge>
                    <h6 className="mt-2">{offer.guarantee?.headline}</h6>
                    <p className="mb-0">{offer.guarantee?.description}</p>
                  </Card.Body>
                </Card>

                {/* Urgency */}
                <Card className="mb-4">
                  <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">Urgency</h5>
                  </Card.Header>
                  <Card.Body>
                    <Badge bg="warning" text="dark" className="mb-2">{offer.urgency?.mechanism}</Badge>
                    <h6 className="mt-2">{offer.urgency?.headline}</h6>
                    <p className="mb-0">{offer.urgency?.description}</p>
                  </Card.Body>
                </Card>

                {/* Risk Reversal */}
                <Card className="mb-4">
                  <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">Risk Reversal</h5>
                  </Card.Header>
                  <Card.Body>
                    <h6>{offer.risk_reversal?.strategy}</h6>
                    <p className="mb-0">{offer.risk_reversal?.copy}</p>
                  </Card.Body>
                </Card>

                {/* Price Anchoring */}
                <Card className="mb-4">
                  <Card.Header className="bg-secondary text-white">
                    <h5 className="mb-0">Price Anchoring</h5>
                  </Card.Header>
                  <Card.Body>
                    <Badge bg="success" className="mb-2">Total Value: {offer.price_anchoring?.total_value}</Badge>
                    <p className="text-muted mb-2"><small>{offer.price_anchoring?.approach}</small></p>
                    <p className="mb-0">{offer.price_anchoring?.copy}</p>
                  </Card.Body>
                </Card>

                <div className="d-flex gap-3 justify-content-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleCustomize}
                  >
                    Customize My Offer →
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={handleGenerate}
                  >
                    Regenerate
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Screen 4: Customize
  if (stage === 'customizing' && offer) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>Customize Your Offer</h2>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back to Dashboard
                  </Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {/* Complete Unified Offer */}
                <Card className="mb-4 border-warning border-2">
                  <Card.Header className="bg-dark text-white">
                    <h5 className="mb-0">Complete Offer Copy</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Label>Your Complete Offer (as it will appear to customers)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={offer.complete_offer || ''}
                        onChange={(e) => setOffer({ ...offer, complete_offer: e.target.value })}
                        placeholder="Get [Product] + [Bonus 1] + [Bonus 2]... (Total Value: $X,XXX) for just $XXX. Plus [Guarantee]. [Urgency]."
                      />
                      <Form.Text className="text-muted">
                        This is the unified offer copy that ties everything together. Edit it to flow naturally.
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                </Card>

                <Alert variant="info" className="mb-4">
                  <strong>Individual Components:</strong> The sections below let you customize each building block of your offer.
                </Alert>

                {/* Bonus Stack */}
                <Card className="mb-4">
                  <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Bonus Stack</h5>
                    <Button variant="light" size="sm" onClick={addBonus}>+ Add Bonus</Button>
                  </Card.Header>
                  <Card.Body>
                    {offer.bonus_stack && offer.bonus_stack.map((bonus, index) => (
                      <Card key={index} className="mb-3">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h6>Bonus {index + 1}</h6>
                            {offer.bonus_stack.length > 1 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeBonus(index)}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                          <Form.Group className="mb-3">
                            <Form.Label>Bonus Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={bonus.name}
                              onChange={(e) => updateBonus(index, 'name', e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={bonus.description}
                              onChange={(e) => updateBonus(index, 'description', e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-0">
                            <Form.Label>Perceived Value</Form.Label>
                            <Form.Control
                              type="text"
                              value={bonus.perceived_value}
                              onChange={(e) => updateBonus(index, 'perceived_value', e.target.value)}
                              placeholder="$297"
                            />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    ))}
                  </Card.Body>
                </Card>

                {/* Guarantee */}
                <Card className="mb-4">
                  <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">Guarantee</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Type</Form.Label>
                      <Form.Control
                        type="text"
                        value={offer.guarantee?.type || ''}
                        onChange={(e) => setOffer({ ...offer, guarantee: { ...offer.guarantee, type: e.target.value } })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Headline</Form.Label>
                      <Form.Control
                        type="text"
                        value={offer.guarantee?.headline || ''}
                        onChange={(e) => setOffer({ ...offer, guarantee: { ...offer.guarantee, headline: e.target.value } })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={offer.guarantee?.description || ''}
                        onChange={(e) => setOffer({ ...offer, guarantee: { ...offer.guarantee, description: e.target.value } })}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Urgency */}
                <Card className="mb-4">
                  <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">Urgency</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Mechanism</Form.Label>
                      <Form.Control
                        type="text"
                        value={offer.urgency?.mechanism || ''}
                        onChange={(e) => setOffer({ ...offer, urgency: { ...offer.urgency, mechanism: e.target.value } })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Headline</Form.Label>
                      <Form.Control
                        type="text"
                        value={offer.urgency?.headline || ''}
                        onChange={(e) => setOffer({ ...offer, urgency: { ...offer.urgency, headline: e.target.value } })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={offer.urgency?.description || ''}
                        onChange={(e) => setOffer({ ...offer, urgency: { ...offer.urgency, description: e.target.value } })}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Risk Reversal */}
                <Card className="mb-4">
                  <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">Risk Reversal</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Strategy</Form.Label>
                      <Form.Control
                        type="text"
                        value={offer.risk_reversal?.strategy || ''}
                        onChange={(e) => setOffer({ ...offer, risk_reversal: { ...offer.risk_reversal, strategy: e.target.value } })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>Copy</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={offer.risk_reversal?.copy || ''}
                        onChange={(e) => setOffer({ ...offer, risk_reversal: { ...offer.risk_reversal, copy: e.target.value } })}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Price Anchoring */}
                <Card className="mb-4">
                  <Card.Header className="bg-secondary text-white">
                    <h5 className="mb-0">Price Anchoring</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Value</Form.Label>
                      <Form.Control
                        type="text"
                        value={offer.price_anchoring?.total_value || ''}
                        onChange={(e) => setOffer({ ...offer, price_anchoring: { ...offer.price_anchoring, total_value: e.target.value } })}
                        placeholder="$2,500"
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Approach</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={offer.price_anchoring?.approach || ''}
                        onChange={(e) => setOffer({ ...offer, price_anchoring: { ...offer.price_anchoring, approach: e.target.value } })}
                      />
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>Copy</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={offer.price_anchoring?.copy || ''}
                        onChange={(e) => setOffer({ ...offer, price_anchoring: { ...offer.price_anchoring, copy: e.target.value } })}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                <div className="d-flex gap-3 justify-content-center">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('recommendations')}
                  >
                    ← Back to Review
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setStage('guarantee')}
                  >
                    Regenerate
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Irresistible Offer →'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default IrresistibleOffer;
